import React from "react";
import { motion } from "framer-motion";
import * as Lucide from "lucide-react";
import { Link } from "react-router-dom";
import {
  TRUST_COMPANIES,
  PROBLEMS,
  SOLUTIONS,
  AI_ENGINE,
  SERVICES,
  HOT_ROLES,
  ROADMAP,
  TESTIMONIALS,
  FAQS,
} from "@/lib/site-data";
import { ArrowRight, MessageCircle, X, Check, Quote, Star } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/site-data";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const Section = ({ id, eyebrow, title, kicker, children, ...rest }) => (
  <section id={id} className="relative py-20 md:py-24 overflow-hidden" {...rest}>
    <div className="max-w-7xl mx-auto px-5 sm:px-6">
      {(eyebrow || title) && (
        <div className="max-w-3xl mb-10 md:mb-14">
          {eyebrow && (
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#9db1ff]">
              {eyebrow}
            </p>
          )}
          {title && (
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tighter leading-[1.05]">
              {title}
            </h2>
          )}
          {kicker && <p className="mt-4 text-zinc-400 text-lg leading-relaxed">{kicker}</p>}
        </div>
      )}
      {children}
    </div>
  </section>
);

export const TrustStrip = () => (
  <section className="py-10 border-y border-white/[0.05] bg-black/40" data-testid="trust-strip">
    <div className="max-w-7xl mx-auto px-6">
      <p className="text-center text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">
        Students placed at companies like
      </p>
      <div className="marquee-mask overflow-hidden">
        <div className="flex gap-16 animate-marquee whitespace-nowrap">
          {[...TRUST_COMPANIES, ...TRUST_COMPANIES].map((c, i) => (
            <span
              key={`${c}-${i}`}
              className="font-heading text-2xl md:text-3xl font-medium text-zinc-400 hover:text-white transition-colors"
            >
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const ProblemSolution = () => (
  <Section
    eyebrow="The problem · the solution"
    title="Stop guessing. Start landing US offers."
    kicker="Most candidates struggle for months. Xyro replaces that chaos with an AI-powered system built specifically for the US market."
  >
    <div className="grid md:grid-cols-2 gap-6">
      <div className="glass-prism p-8 md:p-10">
        <p className="text-xs uppercase tracking-[0.2em] text-red-400/80 font-semibold">Struggling with</p>
        <h3 className="font-heading text-2xl text-white mt-3">The old way</h3>
        <ul className="mt-6 space-y-3">
          {PROBLEMS.map((p) => (
            <li key={p} className="flex items-start gap-3 text-zinc-300">
              <span className="mt-1 flex-none w-5 h-5 rounded-full bg-red-500/15 border border-red-400/30 flex items-center justify-center">
                <X className="w-3 h-3 text-red-400" />
              </span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="glass-prism p-8 md:p-10 border border-[#2B4DFF]/30">
        <p className="text-xs uppercase tracking-[0.2em] text-[#9db1ff] font-semibold">Xyro helps you with</p>
        <h3 className="font-heading text-2xl text-white mt-3">The Xyro way</h3>
        <ul className="mt-6 space-y-3">
          {SOLUTIONS.map((p) => (
            <li key={p} className="flex items-start gap-3 text-white">
              <span className="mt-1 flex-none w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                <Check className="w-3 h-3 text-emerald-400" />
              </span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </Section>
);

export const AIEngine = () => (
  <Section
    id="ai-engine"
    eyebrow="Meet Xyro AI Career Engine"
    title="An AI co-pilot for your US job search."
    kicker="Five intelligent modules built for the modern US hiring stack."
  >
    <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
      {AI_ENGINE.map((it, i) => {
        const Icon = Lucide[it.icon] || Lucide.Sparkles;
        const span = i === 0 ? "md:col-span-3" : i === 1 ? "md:col-span-3" : "md:col-span-2";
        return (
          <motion.div
            key={it.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            className={`glass-prism p-7 md:p-8 group hover:-translate-y-1 transition-transform h-full flex flex-col ${span}`}
            data-testid={`ai-engine-card-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-[#2B4DFF]/15 border border-[#2B4DFF]/30 flex items-center justify-center mb-5">
              <Icon className="w-5 h-5 text-[#9db1ff]" />
            </div>
            <h3 className="font-heading text-xl md:text-2xl text-white">{it.title}</h3>
            <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{it.desc}</p>
          </motion.div>
        );
      })}
    </div>
  </Section>
);

export const ServicesGrid = () => (
  <Section
    id="services"
    eyebrow="What we do"
    title="End-to-end US placement services."
    kicker="Twelve focused services across consultation, optimization, preparation and placement."
  >
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {SERVICES.map((s, i) => {
        const Icon = Lucide[s.icon] || Lucide.Sparkles;
        return (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
            className="glass p-6 hover:border-white/20 hover:-translate-y-1 transition-all h-full"
            data-testid={`service-card-${i}`}
          >
            <Icon className="w-5 h-5 text-[#9db1ff] mb-4" />
            <h3 className="font-heading text-lg text-white">{s.title}</h3>
            <p className="mt-1.5 text-sm text-zinc-400">{s.desc}</p>
          </motion.div>
        );
      })}
    </div>
  </Section>
);

export const HotRolesSection = () => (
  <Section
    id="hot-roles"
    eyebrow="Hot Career Opportunities"
    title="In-demand US roles. Real salary ranges."
    kicker="We actively place candidates across the highest-paying tech roles in the US market."
  >
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {HOT_ROLES.map((r, i) => (
        <motion.div
          key={r.role}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
          className="glass-prism p-6 group hover:-translate-y-1 transition-all h-full flex flex-col"
          data-testid={`role-card-${i}`}
        >
          <div className="flex items-start justify-between">
            <h3 className="font-heading text-lg md:text-xl text-white">{r.role}</h3>
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${
                r.demand === "Explosive"
                  ? "border-violet-400/40 text-violet-300 bg-violet-500/10"
                  : r.demand === "Very High"
                  ? "border-[#5b78ff]/40 text-[#9db1ff] bg-[#2B4DFF]/10"
                  : "border-emerald-400/40 text-emerald-300 bg-emerald-500/10"
              }`}
            >
              {r.demand}
            </span>
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.18em] text-zinc-500">Salary range</p>
          <p className="font-heading text-2xl text-white mt-1">{r.salary}</p>
          <Link
            to="/contact"
            className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#9db1ff] hover:text-white"
          >
            Get hired for this role <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
      ))}
    </div>
  </Section>
);

export const RoadmapSection = () => (
  <Section
    id="roadmap"
    eyebrow="The Xyro success roadmap"
    title="Nine deliberate steps to your US offer."
    kicker="A proven, AI-augmented path from first call to signed offer letter."
  >
    <div className="relative grid md:grid-cols-3 gap-6">
      {ROADMAP.map((s, i) => (
        <motion.div
          key={s.step}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
          className="glass p-7 relative h-full"
          data-testid={`roadmap-step-${s.step}`}
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-xs text-[#9db1ff]">
              STEP {String(s.step).padStart(2, "0")}
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <h3 className="font-heading text-xl text-white">{s.title}</h3>
          <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#5b78ff] shadow-[0_0_18px_rgba(43,77,255,0.8)]" />
        </motion.div>
      ))}
    </div>
  </Section>
);

export const TestimonialsSection = () => (
  <Section
    id="success-stories"
    eyebrow="Success stories"
    title="Real candidates. Real US offers."
    kicker="Five recent placements who turned their job search around with Xyro."
  >
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {TESTIMONIALS.map((t, i) => (
        <motion.figure
          key={t.name}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
          className={`glass-prism p-7 md:p-8 ${i === 0 ? "lg:row-span-2" : ""}`}
          data-testid={`testimonial-${i}`}
        >
          <Quote className="w-6 h-6 text-[#5b78ff] mb-4" />
          <blockquote className="text-white text-base leading-relaxed">{t.review}</blockquote>
          <figcaption className="mt-6 pt-5 border-t border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2B4DFF] to-[#8A2BE2] flex items-center justify-center text-white font-semibold">
                {t.name.split(" ").map((n) => n[0]).join("")}
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{t.name}</p>
                <p className="text-xs text-zinc-400">
                  {t.role} · {t.company}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="font-mono text-sm text-emerald-300">{t.package}</span>
            </div>
          </figcaption>
        </motion.figure>
      ))}
    </div>
  </Section>
);

export const FAQSection = () => (
  <Section
    id="faq"
    eyebrow="Frequently asked questions"
    title="Everything you need to know."
    kicker="Twenty answers covering OPT, CPT, H1B, placements, resume, LinkedIn, interviews, AI tools and career guidance."
  >
    <div className="glass p-6 md:p-10">
      <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="border-white/[0.06]">
            <AccordionTrigger
              data-testid={`faq-trigger-${i}`}
              className="text-left text-white hover:no-underline font-heading text-base md:text-lg"
            >
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-zinc-400 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </Section>
);

export const FinalCTA = () => (
  <section className="relative py-20 md:py-24" data-testid="final-cta">
    <div className="max-w-5xl mx-auto px-6">
      <div className="relative glass-prism p-10 md:p-16 text-center overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-60"
          style={{ background: "radial-gradient(circle, rgba(43,77,255,0.4), transparent 70%)", filter: "blur(80px)" }}
        />
        <h2 className="relative font-heading text-4xl md:text-6xl font-semibold text-white tracking-tighter leading-[1.05]">
          Your dream US career <br />
          <span className="text-gradient-electric">starts here.</span>
        </h2>
        <p className="relative mt-5 text-zinc-400 max-w-2xl mx-auto text-lg">
          Stop guessing. Start building a strategy that gets results.
        </p>
        <div className="relative mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            data-testid="final-cta-book"
            className="btn-primary-xyro inline-flex items-center gap-2 rounded-full px-7 py-4 font-medium"
          >
            Book Free Career Consultation <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="final-cta-whatsapp"
            className="btn-ghost-xyro inline-flex items-center gap-2 rounded-full px-7 py-4 font-medium"
          >
            <MessageCircle className="w-4 h-4 text-emerald-400" /> Chat On WhatsApp
          </a>
        </div>
      </div>
    </div>
  </section>
);
